/**
 * Panel de la pestaña "Equipo": 6 slots de equipamiento arriba + grilla de
 * inventario abajo (con scroll táctil). Cada pieza del inventario se
 * agrupa por set+slot+tier; tocar una pila con 3+ piezas la fusiona
 * (tap-tap-tap), tocar cualquier otra la equipa en su slot.
 */

import Phaser from 'phaser';
import type { GearSlot, MetaState } from '../../core/types';
import { gear as gearDefs } from '../../data';
import type { Rng } from '../../core/rng';
import { type GearGroup, canMergeGroup, equipGear, groupGear, mergeGear, unequipSlot } from '../../meta';
import { PALETTE, rarityColor, slotColor, slotLabel } from './palette';

const SLOT_ORDER: GearSlot[] = ['weapon', 'ring', 'helm', 'chest', 'gloves', 'boots'];

function gearDefFor(setKey: string, slot: GearSlot) {
  return gearDefs.find((d) => d.setKey === setKey && d.slot === slot);
}

export class GearPanel extends Phaser.GameObjects.Container {
  private meta: MetaState;
  private maskGraphics: Phaser.GameObjects.Graphics | null = null;
  private scrollY = 0;

  constructor(
    scene: Phaser.Scene,
    originX: number,
    originY: number,
    private readonly panelWidth: number,
    private readonly panelHeight: number,
    meta: MetaState,
    private readonly rng: Rng,
    private readonly onChange: (nextMeta: MetaState) => void
  ) {
    super(scene, originX, originY);
    this.meta = meta;
    scene.add.existing(this);
    this.build();
  }

  private build(): void {
    this.removeAll(true); // destruye todo lo anterior (incluye zonas interactivas/arrastrables)
    this.maskGraphics?.destroy();

    const title = this.scene.add
      .text(16, 10, 'EQUIPAMIENTO', {
        fontFamily: 'monospace',
        fontSize: '15px',
        color: '#f4f0ff',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);
    this.add(title);

    this.buildEquipSlots();

    const invTitle = this.scene.add
      .text(16, 138, 'INVENTARIO (toca 3 iguales para fusionar)', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#a99fd1',
      })
      .setOrigin(0, 0);
    this.add(invTitle);

    this.buildInventory();
  }

  private buildEquipSlots(): void {
    const size = 60;
    const gap = 8;
    const total = SLOT_ORDER.length * size + (SLOT_ORDER.length - 1) * gap;
    const startX = (this.panelWidth - total) / 2 + size / 2;
    const y = 66;

    SLOT_ORDER.forEach((slot, i) => {
      const cx = startX + i * (size + gap);
      const gearId = this.meta.equipped[slot];
      const piece = gearId ? this.meta.gear.find((g) => g.id === gearId) : undefined;
      const def = piece ? gearDefFor(piece.setKey, piece.slot) : undefined;
      const rarity = def && piece ? def.rarityByTier[piece.tier - 1] ?? 'comun' : undefined;
      const border = rarity ? rarityColor(rarity) : PALETTE.borderLight;

      const bg = this.scene.add.graphics();
      bg.fillStyle(piece ? PALETTE.panelLight : PALETTE.panel2, 1);
      bg.fillRoundedRect(cx - size / 2, y - size / 2, size, size, 10);
      bg.lineStyle(piece ? 3 : 2, border, piece ? 1 : 0.6);
      bg.strokeRoundedRect(cx - size / 2 + 1, y - size / 2 + 1, size - 2, size - 2, 10);

      const glyph = this.scene.add
        .text(cx, y - 6, piece ? `T${piece.tier}` : slotLabel(slot).slice(0, 2).toUpperCase(), {
          fontFamily: 'monospace',
          fontSize: '13px',
          color: piece ? '#f4f0ff' : '#6f6394',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);

      const label = this.scene.add
        .text(cx, y + size / 2 + 10, slotLabel(slot), {
          fontFamily: 'monospace',
          fontSize: '9px',
          color: '#a99fd1',
        })
        .setOrigin(0.5);

      const zone = this.scene.add.zone(cx, y, size, size).setInteractive({ useHandCursor: true });
      zone.on('pointerdown', () => {
        if (!piece) return;
        this.applyChange(unequipSlot(this.meta, slot));
      });

      this.add([bg, glyph, label, zone]);
    });
  }

  private buildInventory(): void {
    const viewportX = 16;
    const viewportY = 156;
    const viewportW = this.panelWidth - 32;
    const viewportH = Math.max(60, this.panelHeight - viewportY - 8);

    const groups = groupGear(this.meta.gear);

    if (groups.length === 0) {
      const empty = this.scene.add
        .text(this.panelWidth / 2, viewportY + 24, 'Todavía no tienes gear.\nGánalo terminando runs.', {
          fontFamily: 'monospace',
          fontSize: '12px',
          color: '#6f6394',
          align: 'center',
        })
        .setOrigin(0.5, 0);
      this.add(empty);
      return;
    }

    const columns = 4;
    const gap = 10;
    const tileSize = (viewportW - gap * (columns - 1)) / columns;

    const content = this.scene.add.container(viewportX, viewportY);
    groups.forEach((group, i) => {
      const col = i % columns;
      const row = Math.floor(i / columns);
      const x = col * (tileSize + gap);
      const y = row * (tileSize + gap);
      content.add(this.buildGearTile(group, x, y, tileSize));
    });
    this.add(content);

    const contentHeight = Math.ceil(groups.length / columns) * (tileSize + gap);
    const maxScroll = Math.max(0, contentHeight - viewportH);
    this.scrollY = Math.min(this.scrollY, maxScroll);
    content.y = viewportY - this.scrollY;

    // máscara de recorte: recorta el contenido al área del viewport.
    const maskGraphics = this.scene.make.graphics({ x: this.x + viewportX, y: this.y + viewportY });
    maskGraphics.fillStyle(0xffffff, 1);
    maskGraphics.fillRect(0, 0, viewportW, viewportH);
    const mask = maskGraphics.createGeometryMask();
    content.setMask(mask);
    this.maskGraphics = maskGraphics;

    if (maxScroll > 0) {
      const dragZone = this.scene.add
        .zone(viewportX + viewportW / 2, viewportY + viewportH / 2, viewportW, viewportH)
        .setInteractive({ draggable: true });
      let startPointerY = 0;
      let startScroll = 0;
      dragZone.on('dragstart', (pointer: Phaser.Input.Pointer) => {
        startPointerY = pointer.y;
        startScroll = this.scrollY;
      });
      dragZone.on('drag', (pointer: Phaser.Input.Pointer) => {
        const delta = pointer.y - startPointerY;
        this.scrollY = Phaser.Math.Clamp(startScroll - delta, 0, maxScroll);
        content.y = viewportY - this.scrollY;
      });
      this.add(dragZone);
    }
  }

  private buildGearTile(group: GearGroup, x: number, y: number, size: number): Phaser.GameObjects.Container {
    const def = gearDefFor(group.setKey, group.slot);
    const rarity = def?.rarityByTier[group.tier - 1] ?? 'comun';
    const border = rarityColor(rarity);
    const mergeable = canMergeGroup(group);
    const equippedId = this.meta.equipped[group.slot];
    const isEquipped = group.pieces.some((p) => p.id === equippedId);

    const container = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(slotColor(group.slot), 0.22);
    bg.fillRoundedRect(0, 0, size, size, 10);
    bg.lineStyle(mergeable ? 3 : 2, mergeable ? PALETTE.accent : border, 1);
    bg.strokeRoundedRect(1, 1, size - 2, size - 2, 10);

    const slotText = this.scene.add
      .text(size / 2, size * 0.32, slotLabel(group.slot), {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#f4f0ff',
      })
      .setOrigin(0.5);

    const tierText = this.scene.add
      .text(size / 2, size * 0.55, `Tier ${group.tier}`, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#ffd166',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    container.add([bg, slotText, tierText]);

    if (group.pieces.length > 1) {
      const badge = this.scene.add
        .text(size - 8, 8, `x${group.pieces.length}`, {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#f4f0ff',
          backgroundColor: '#1b1030',
        })
        .setOrigin(1, 0);
      container.add(badge);
    }

    if (mergeable) {
      const mergeHint = this.scene.add
        .text(size / 2, size - 12, 'FUSIONAR', {
          fontFamily: 'monospace',
          fontSize: '9px',
          color: '#241c3d',
          backgroundColor: '#ffb703',
          padding: { x: 4, y: 1 },
        })
        .setOrigin(0.5);
      container.add(mergeHint);
    } else if (isEquipped) {
      const equippedHint = this.scene.add
        .text(6, 8, 'EQUIPADO', {
          fontFamily: 'monospace',
          fontSize: '8px',
          color: '#6fcf6f',
        })
        .setOrigin(0, 0);
      container.add(equippedHint);
    }

    const zone = this.scene.add.zone(size / 2, size / 2, size, size).setInteractive({ useHandCursor: true });
    zone.on('pointerdown', () => this.handleTileTap(group));
    container.add(zone);

    return container;
  }

  private handleTileTap(group: GearGroup): void {
    if (canMergeGroup(group)) {
      const nextGear = mergeGear(this.meta.gear, group.setKey, group.slot, group.tier, this.rng);
      this.applyChange({ ...this.meta, gear: nextGear });
      return;
    }
    const piece = group.pieces[0];
    if (!piece) return;
    this.applyChange(equipGear(this.meta, piece.id));
  }

  private applyChange(nextMeta: MetaState): void {
    this.meta = nextMeta;
    this.onChange(nextMeta);
  }

  /** Reconstruye el panel con un meta actualizado (p. ej. tras comprar/ganar gear en otra pantalla). */
  refresh(meta: MetaState): void {
    this.meta = meta;
    this.build();
  }
}
